// 문의 관리 (기획안 v1.2 · 시안 v6, 백엔드 v2 API). 데이터·상태는 여기서 가지고, 아래 컴포넌트는 그리기만 한다.
// 상세 서랍과 전화 등록 창은 각자 API를 부르고, 끝나면 onChanged 로 숫자·목록을 다시 읽게 한다.
import { useCallback, useEffect, useMemo, useState } from "react";
import { showToast } from "../../utill/utill";
import * as api from "./v6Api";
import { EMPTY_FILTER, filterToParams } from "./view";
import { runAction } from "./actions";
import useAdminSocket from "./useAdminSocket";
import { getToken, isOwner, clearAuth, emitAuth } from "../v6common/auth";
import StatsCards from "./StatsCards";
import FilterBar from "./FilterBar";
import InquiryTable from "./InquiryTable";
import StaffBoard from "./StaffBoard";
import AdminsTab from "./AdminsTab";
import InquiryDrawer from "./InquiryDrawer";
import PhoneInquiryModal from "./PhoneInquiryModal";
import NotifPanel from "./NotifPanel";
import "../../admin_styles/inquiry_renewal.css";

const SEARCH_DEBOUNCE_MS = 300; // 검색어는 입력이 멈춘 뒤 한 번만 요청한다
const SUMMARY_EVERY_MS = 30000; // 소켓은 빠른 길일 뿐, 숫자는 이 주기 조회로 맞춘다(계약 §4-2·§5)

// me: 로그인한 직원(계약 §2 StaffMe). params.openId: 다른 메뉴(대시보드·대화 기록)에서 특정 문의를 열고 들어올 때
export default function InquiryPage({ me, params, go }) {
    const adminId = me?.id ?? null;
    const ready = Number.isInteger(adminId);
    const owner = isOwner(me);
    const [meta, setMeta] = useState(null);
    const [staff, setStaff] = useState([]);
    const [summary, setSummary] = useState(null);
    const [list, setList] = useState({ items: [], total: 0 });
    const [board, setBoard] = useState(null);
    const [team, setTeam] = useState("전체");
    const [view, setView] = useState("list");
    const [filter, setFilter] = useState(EMPTY_FILTER);
    const [selId, setSelId] = useState(params?.openId ?? null);
    const [detailKey, setDetailKey] = useState(0);
    const [phoneOpen, setPhoneOpen] = useState(false);
    const [notif, setNotif] = useState(null); // null = 닫힘, 아니면 { items: 안 읽은 문의, posts: 가맹점주 새 글 }
    const [tick, setTick] = useState(0); // 목록·보드 다시 읽기 신호
    const [q, setQ] = useState(filter.q);
    useEffect(() => {
        const t = setTimeout(() => setQ(filter.q), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [filter.q]);
    // 요청에 쓰는 필터: 검색어만 늦춰 반영. 문자열 열쇠로 같은 조건이면 다시 요청하지 않는다
    const queryKey = JSON.stringify({ ...filter, q });

    useEffect(() => { if (params?.openId != null) setSelId(params.openId); }, [params]);

    useEffect(() => {
        if (!ready) return;
        api.getMeta().then(setMeta).catch((e) => showToast(api.errorText(e), "error"));
        api.getStaffOptions().then(setStaff).catch(() => {});
    }, [ready]);

    const loadSummary = useCallback(() => (ready ? api.getSummary().then(setSummary).catch(() => {}) : Promise.resolve()), [ready]);
    useEffect(() => {
        if (!ready) return undefined;
        loadSummary();
        const timer = setInterval(loadSummary, SUMMARY_EVERY_MS);
        const onVisible = () => { if (document.visibilityState === "visible") loadSummary(); };
        document.addEventListener("visibilitychange", onVisible);
        return () => { clearInterval(timer); document.removeEventListener("visibilitychange", onVisible); };
    }, [ready, loadSummary]);

    // 늦게 온 이전 응답이 새 조건의 결과를 덮지 않도록 alive 로 버린다
    useEffect(() => {
        if (!ready || !meta || view !== "list") return undefined;
        let alive = true;
        const p = filterToParams(JSON.parse(queryKey), meta);
        p.set("size", "200");
        api.getList(p).then((r) => alive && setList(r)).catch((e) => alive && showToast(api.errorText(e), "error"));
        return () => { alive = false; };
    }, [ready, meta, queryKey, view, tick]);

    useEffect(() => {
        if (!ready || !meta || view !== "board") return undefined;
        let alive = true;
        api.getBoard(filterToParams(JSON.parse(queryKey), meta, { forBoard: true }), team)
            .then((r) => alive && setBoard(r)).catch((e) => alive && showToast(api.errorText(e), "error"));
        return () => { alive = false; };
    }, [ready, meta, queryKey, view, team, tick]);

    const refresh = useCallback(() => { loadSummary(); setTick((t) => t + 1); }, [loadSummary]);

    useAdminSocket(ready ? getToken() : null, (msg) => {
        if (msg.summary) setSummary(msg.summary);
        if (msg.type === "inquiry.created") {
            const q = msg.inquiry;
            if (q?.channel === "chatbot") showToast(`새 챗봇 문의 · ${q.store?.name} · ${q.subcategory?.label}`, "info");
            setTick((t) => t + 1);
        } else if (msg.type === "inquiry.updated") {
            setTick((t) => t + 1);
            if (msg.by?.type === "customer") showToast(`가맹점주 새 글 · No.${msg.inquiry_id}`, "info");
            if (msg.inquiry_id === selId && (msg.by?.type !== "staff" || msg.by?.id !== adminId)) setDetailKey((k) => k + 1);
        }
    }, () => { clearAuth(); emitAuth("login"); });

    const open = (id) => { setSelId(id); setNotif(null); setPhoneOpen(false); };
    const toggleNotif = () => {
        if (notif) { setNotif(null); return; }
        const p = new URLSearchParams([["unread", "true"], ["status", "all"], ["size", "20"]]);
        Promise.all([api.getList(p).catch(() => ({ items: [] })), api.getNewPosts().catch(() => ({ items: [] }))])
            .then(([u, c]) => setNotif({ items: u.items || [], posts: c.items || [] }));
    };

    const onMove = (card, a) => runAction(
        () => (a.kind === "add" ? api.addAssignee(card.id, a.to) : a.kind === "remove" ? api.removeAssignee(card.id, a.from) : api.transferAssignee(card.id, a.from, a.to)),
        { notify: showToast, reload: refresh },
    );

    const onCreated = (detail, warning) => {
        setPhoneOpen(false);
        refresh();
        if (warning) { showToast(warning, "warning"); setSelId(detail.id); }
        else showToast(`No.${detail.id} ${detail.store?.name || ""} 문의를 등록했습니다.`, "success");
    };

    const downloadCsv = async () => {
        try {
            const blob = await api.exportCsv(filterToParams(filter, meta));
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `문의목록_${new Date().toLocaleDateString("sv-SE", { timeZone: "Asia/Seoul" })}.csv`;
            a.click();
            setTimeout(() => URL.revokeObjectURL(a.href), 1000);
            showToast(`지금 보이는 ${list.total}건을 엑셀(CSV)로 내려받았습니다.`, "success");
        } catch (e) {
            showToast(api.errorText(e), "error");
        }
    };

    const teams = useMemo(() => ["전체", ...new Set((staff || []).filter((s) => s.active).map((s) => s.team || "소속 없음"))], [staff]);
    const unread = (summary?.unread || 0) + (summary?.customer_new_post || 0); // 종 점 = 안 읽음 + 가맹점주 새 글

    return (
        <main className="inq-r">
            <div className="top">
                <div><h1>문의 관리</h1><p>챗봇으로 들어온 문의와 전화로 받은 문의를 한곳에서 접수·배정·처리합니다</p></div>
                <div className="top-act" style={{ position: "relative" }}>
                    <button className="btn bell" title="안 읽은 문의" aria-label="안 읽은 문의" disabled={!ready} onClick={toggleNotif}>
                        <i className="fa-regular fa-bell"></i>{unread > 0 && <span className="dot">{unread > 99 ? "99+" : unread}</span>}
                    </button>
                    {notif && <NotifPanel items={notif.items} posts={notif.posts} onOpen={open} />}
                    <button className="btn" disabled={!ready || !meta} onClick={downloadCsv}><i className="fa-solid fa-file-excel" style={{ color: "#15803d" }}></i>엑셀</button>
                    <button className="btn btn-pri" disabled={!ready || !meta} onClick={() => setPhoneOpen(true)}><i className="fa-solid fa-phone"></i>전화 문의 등록</button>
                </div>
            </div>
            {ready && meta && (
                <StatsCards summary={summary} meta={meta} filter={filter}
                    onPickStatus={(code) => setFilter((f) => ({ ...f, statuses: f.statuses?.length === 1 && f.statuses[0] === code ? null : [code], assignee: "" }))}
                    onPickUnassigned={() => setFilter((f) => ({ ...f, assignee: f.assignee === "none" ? "" : "none", statuses: null }))} />
            )}
            <div className="panel">
                <div className="tabs">
                    {[["list", "fa-table-list", "목록"], ["board", "fa-table-columns", "직원별 업무"], ...(owner ? [["admins", "fa-users-gear", "관리자"]] : [])].map(([k, ic, l]) => (
                        <button key={k} className={`tab ${view === k ? "on" : ""}`} disabled={!ready} onClick={() => setView(k)}>
                            <i className={`fa-solid ${ic}`}></i>{l}</button>
                    ))}
                    <span className="sp"></span>
                </div>
                {ready && meta && view !== "admins" && <FilterBar filter={filter} onChange={setFilter} meta={meta} staff={staff} />}
                {ready && meta && view === "list" && (
                    <InquiryTable items={list.items} total={list.total} grandTotal={summary?.total ?? list.total} selId={selId} meta={meta} onOpen={open} />
                )}
                {ready && meta && view === "board" && (
                    <StaffBoard board={board} meta={meta} team={team} teams={teams} onTeam={setTeam} onMove={onMove} onOpen={open} />
                )}
                {owner && view === "admins" && <AdminsTab me={me} meta={meta} notify={showToast} />}
            </div>
            {ready && meta && selId !== null && (
                <InquiryDrawer key={selId} id={selId} meta={meta} staff={staff} adminId={adminId} owner={owner} refreshKey={detailKey}
                    onClose={() => setSelId(null)} onOpen={open} onChanged={refresh} notify={showToast}
                    onGoChat={owner && go ? (sid) => go("chat_history", { sessionId: sid }) : undefined} />
            )}
            {ready && meta && phoneOpen && (
                <PhoneInquiryModal meta={meta} staff={staff} adminId={adminId} onClose={() => setPhoneOpen(false)} onCreated={onCreated} onOpenExisting={open} />
            )}
        </main>
    );
}
